import { elevation, fontWeights, layout, MIN_TOUCH_TARGET, motion, radii, spacing, typography, } from '@lubehub/theme';
/**
 * The bridge between the shared tokens and CSS.
 *
 * Every token becomes a CSS custom property, and every component in this
 * package styles itself with `var(--lh-…)` and nothing else. That is what makes
 * "one design system across mobile and web" a property of the build rather than
 * a matter of discipline: a colour changed in `@lubehub/theme` moves both
 * surfaces, and a component *cannot* quietly introduce a ninth grey, because
 * there is no literal colour anywhere for it to introduce one as.
 *
 * `src/__tests__/tokens.test.ts` enforces exactly that by reading `styles.css`
 * and failing on any hard-coded colour or pixel value outside the scale.
 *
 * Custom properties rather than a compiled stylesheet per theme: switching to
 * dark is then a single re-render of the provider's `style` object, with no
 * flash, no second stylesheet to load, and no class names to keep in sync.
 */
/**
 * Control heights.
 *
 * `md` is the shared minimum touch target, so the default button and input are
 * the same size on the web as on the phone. `sm` is deliberately *below* it and
 * exists only for pointer-dense administrative tables, where rows are driven by
 * a mouse; it must not appear on the customer app, where a 36px target is a
 * mis-tap waiting to happen.
 */
const controls = { sm: 36, md: MIN_TOUCH_TARGET, lg: 52 };
/** Prefix on every property, so LubeHub tokens cannot collide with a host page. */
const PREFIX = '--lh';
const px = (value) => `${String(value)}px`;
/**
 * Web needs one `box-shadow` string where React Native takes four properties.
 *
 * The shadow colour comes from the theme rather than a fixed black, because in
 * dark mode a black shadow on a dark surface is invisible and the elevation
 * disappears.
 */
function boxShadow(token, shadowColor) {
    const { shadowOffset, shadowOpacity, shadowRadius } = elevation[token];
    if (shadowOpacity === 0) {
        return 'none';
    }
    // Two layers: a hairline contact shadow that pins the surface to the page,
    // and the soft ambient one the token describes. One blurred layer alone
    // reads as a smudge rather than as depth.
    const contact = ['0', '1px', '2px', withAlpha(shadowColor, Math.min(shadowOpacity, 0.08))].join(' ');
    const ambient = [
        px(shadowOffset.width),
        px(shadowOffset.height),
        px(shadowRadius),
        withAlpha(shadowColor, shadowOpacity),
    ].join(' ');
    return `${contact}, ${ambient}`;
}
/**
 * Applies an opacity to a theme colour.
 *
 * `color-mix()` would be tidier, but it is evaluated by the browser and so
 * cannot be asserted in a unit test; this stays a plain string the tests can
 * read. Hex is what the palette uses, so hex is what is handled — anything else
 * is returned untouched rather than mangled into an invalid colour.
 */
function withAlpha(color, opacity) {
    const digits = /^#([0-9a-f]{6})$/i.exec(color)?.[1];
    if (digits === undefined) {
        return color;
    }
    const alpha = Math.round(Math.min(Math.max(opacity, 0), 1) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${digits}${alpha}`;
}
/**
 * The complete custom-property map for a theme.
 *
 * Returned as a plain object so it can be spread onto a React `style` prop and,
 * in tests, inspected directly.
 */
export function cssVariables(theme) {
    const vars = {};
    for (const [name, value] of Object.entries(theme.colors)) {
        vars[`${PREFIX}-color-${kebab(name)}`] = value;
    }
    for (const [name, value] of Object.entries(spacing)) {
        vars[`${PREFIX}-space-${kebab(name)}`] = px(value);
    }
    for (const [name, value] of Object.entries(radii)) {
        vars[`${PREFIX}-radius-${kebab(name)}`] = px(value);
    }
    for (const [name, value] of Object.entries(fontWeights)) {
        vars[`${PREFIX}-weight-${kebab(name)}`] = value;
    }
    for (const [name, style] of Object.entries(typography)) {
        const key = `${PREFIX}-type-${kebab(name)}`;
        vars[`${key}-size`] = px(style.fontSize);
        // Unitless: a line height in px does not scale when a reader enlarges text.
        vars[`${key}-leading`] = (style.lineHeight / style.fontSize).toFixed(4);
        vars[`${key}-tracking`] = px(style.letterSpacing);
        vars[`${key}-weight`] = fontWeights[style.weight];
    }
    for (const name of Object.keys(elevation)) {
        vars[`${PREFIX}-shadow-${kebab(name)}`] = boxShadow(name, theme.colors.shadow);
    }
    for (const [name, value] of Object.entries(controls)) {
        vars[`${PREFIX}-control-${name}`] = px(value);
    }
    for (const [name, value] of Object.entries(motion.duration)) {
        vars[`${PREFIX}-duration-${kebab(name)}`] = `${String(value)}ms`;
    }
    for (const [name, value] of Object.entries(layout)) {
        vars[`${PREFIX}-layout-${kebab(name)}`] = px(value);
    }
    vars[`${PREFIX}-ease-out`] = `cubic-bezier(${motion.easeOut.join(', ')})`;
    vars[`${PREFIX}-ease-in-out`] = `cubic-bezier(${motion.easeInOut.join(', ')})`;
    vars[`${PREFIX}-count-up-duration`] = `${String(motion.countUp.duration)}ms`;
    vars[`${PREFIX}-busy-delay`] = `${String(motion.busyDelay)}ms`;
    vars[`${PREFIX}-enter-duration`] = `${String(motion.entrance.duration)}ms`;
    vars[`${PREFIX}-enter-stagger`] = `${String(motion.entrance.stagger)}ms`;
    vars[`${PREFIX}-enter-stagger-limit`] = String(motion.entrance.staggerLimit);
    vars[`${PREFIX}-enter-offset`] = px(motion.entrance.offset);
    vars[`${PREFIX}-pulse-duration`] = `${String(motion.pulse.duration)}ms`;
    vars[`${PREFIX}-pulse-min-opacity`] = String(motion.pulse.minOpacity);
    return vars;
}
/** `surfaceSunken` → `surface-sunken`, so the CSS reads like CSS. */
function kebab(name) {
    return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
//# sourceMappingURL=cssVariables.js.map